<template>
  <main class="pb-10 md:pt-10">
    <UiFixedHeader always-visible>
      <template v-if="compilation?.title">
        <div class="flex items-center justify-between">
          <span class="line-clamp-1">{{ compilation?.title }}</span>
          <UiButton
            v-if="smallHeader"
            type="button"
            severity="quaternary"
            class="relative pr-4"
            @click="toggleHideHeader(false)"
          >
            <UiIcon name="three-dots" class="size-6 text-text-main" />
            <div
              v-if="selectedFiltersCount"
              class="absolute -right-0.5 -top-0.5 z-20 flex size-4 items-center justify-center rounded-full text-xxs text-light bg-main"
            >
              {{ selectedFiltersCount }}
            </div>
          </UiButton>
        </div>
      </template>
      <div v-else class="h-6 w-60 animate-pulse rounded-md bg-secondary" />
    </UiFixedHeader>
    <div
      :class="smallHeader ? 'z-20' : 'z-30'"
      class="sticky top-13 bg-primary pl-4 md:top-0 md:pl-0 lg:pb-4"
    >
      <UiContainer class="mb-3 hidden items-center md:flex">
        <UiTitle v-if="compilation?.title" severity="h1" class="!text-base md:!text-3xl">
          {{ compilation?.title }}
        </UiTitle>
        <div v-else class="h-9 w-80 animate-pulse rounded-md bg-secondary" />
      </UiContainer>
      <FilterSearch
        :id="SearchFilterIds.compilationViewSearchFilter"
        :page-size="PAGE_SIZE"
        :show-modal="isMdAndDown"
        class="pb-3 transition-all"
        :class="{ '!absolute -translate-y-full opacity-0': smallHeader }"
        :hide-sorting="disableSort"
        @change="onFiltersChange"
      />
    </div>
    <UiContainer>
      <section v-if="!compilationLoadError" class="relative flex w-full flex-col">
        <CompilationWidget
          :value="compilation"
          :loading="!compilationInitialized"
          type="tiles"
          :grid="gridSize"
          class="-mx-4 lg:-mx-7"
        />
        <UiEternalLoading
          v-if="!allItemsLoaded"
          :handler="loadMoreItems"
          :all-loaded="allItemsLoaded"
        />
        <UiScrollUpButton class="!sticky bottom-20 z-20 ml-auto mr-0 md:bottom-10" />
      </section>
      <UiSearchError
        v-else
        class="flex flex-col items-center py-5"
        @reload="loadCompilation(route.params.slug as string)"
      />
    </UiContainer>
  </main>
</template>

<script setup lang="ts">
import throttle from 'lodash/throttle.js'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount } from 'vue'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import { CompilationWidget } from '@/3_widgets/Compilation'
import { FilterSearch } from '@/4_features/Search'
import { useCompilation, useCompilationGrid } from '@/5_entities/Compilation'
import { useReloadOnConfigurationUpdate } from '@/5_entities/Configuration'
import { SearchFilterIds } from '@/5_entities/Search'
import { useFiltersCount, useHiddenHeader } from '@/6_shared/composables'
import { useBreakpoint } from '@/6_shared/lib'
import type { BaseSearchFilters } from '@/6_shared/model'
import {
  UiButton,
  UiContainer,
  UiEternalLoading,
  UiFixedHeader,
  UiIcon,
  UiScrollUpButton,
  UiSearchError,
  UiTitle
} from '@/6_shared/ui'

const route = useRoute()
const PAGE_SIZE = 24
let filters: BaseSearchFilters = {
  pagination: { pageSize: PAGE_SIZE, currentPage: 1 },
  filter: {}
}
const { selectedFiltersCount, setFiltersCount } = useFiltersCount()
const onFiltersChange = (newFilters: BaseSearchFilters) => {
  setFiltersCount(newFilters)
  filters = newFilters
  loadCompilation(route.params.slug as string)
}
const { getCompilation, loadNextPage } = useCompilation()
const {
  compilation,
  compilationInitialized,
  allItemsLoaded,
  compilationLoadError,
  slugsWithDisabledSort
} = storeToRefs(useCompilation())
const { gridSize } = useCompilationGrid()

const disableSort = computed(
  () => !!slugsWithDisabledSort.value?.includes(route.params.slug as string)
)

const loadCompilation = (slug: string) => getCompilation(slug, filters)
const loadMoreItems = () => loadNextPage(filters)

useReloadOnConfigurationUpdate(() => loadCompilation(route.params.slug as string))
onBeforeMount(() => loadCompilation(route.params.slug as string))

onBeforeRouteUpdate(({ params: { slug: toSlug } }, { params: { slug: fromSlug } }) => {
  if (fromSlug !== toSlug) {
    loadCompilation(toSlug as string)
  }
})

const {
  md: [isMdAndUp, isMdAndDown]
} = useBreakpoint()
const scrollHandler = throttle(function () {
  if (isMdAndUp.value) {
    return toggleHideHeader(false)
  }

  toggleHideHeader(window.scrollY >= minScroll)
})

const { smallHeader, toggleHideHeader, minScroll } = useHiddenHeader(scrollHandler)
</script>

<style scoped>
.calendar-block::after {
  background: linear-gradient(
    180deg,
    var(--colors-gradient-fivefold-first-color),
    var(--colors-gradient-fivefold-second-color) 22%,
    var(--colors-gradient-fivefold-third-color) 100%
  );
}
</style>
